import { useEffect } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Controller, useFormContext } from 'react-hook-form'
import { cn } from '@global/utils/tailwind'
import { TemplatePicker } from './template-picker'
import { FILE_VALIDATION_ERROR } from '../constants'
import { TranslationUploadForm } from '../types'
import { getFileSizeText, validateFiles } from '../utils'
import { DragNDrop } from './drag-n-drop'
import { FileCard } from './file-card'
import { FormError } from './form-error'
import { RadioCard } from './radio-card'
import { StepsFileInfo } from './steps-file-info'
import { StepsInfo } from './steps-info'

export const StepTwo = () => {
  const { control, clearErrors, watch } = useFormContext<TranslationUploadForm>()

  const dataType = watch('stepTwo.dataType')

  useEffect(() => {
    clearErrors('stepTwo.templates')
    clearErrors('stepTwo.files')
  }, [dataType])

  return (
    <div className="bg-white h-[calc(100vh-100px)] overflow-y-auto">
      <div className="flex flex-col max-w-[604px] mx-auto h-full">
        <h3 className="font-bold text-[21px] text-slate-800 mt-[56px] text-center">
          {t`Baseline data`}
        </h3>
        <p className="mt-[8px] mb-[12px] text-slate-800">
          {t`What would you like to use as baseline data? Claravine will compare the data to be translated with the baseline data to create a new, translated taxonomy.`}
        </p>
        <div className="flex gap-[28px] mt-[16px]">
          <Controller
            name="stepTwo.dataType"
            control={control}
            render={({ field: { value, onChange } }) => (
              <>
                <RadioCard
                  title={t`Select templates`}
                  description={t`Select one or many templates in Claravine to use as baseline data`}
                  value="templates"
                  checked={value === 'templates'}
                  onChange={onChange}
                  className="basis-[50%]"
                />
                <RadioCard
                  title={t`Upload .csv files`}
                  description={t`Upload a data dictionary along with any list values`}
                  value="files"
                  checked={value === 'files'}
                  onChange={onChange}
                  className="basis-[50%]"
                />
              </>
            )}
          />
        </div>

        {dataType === 'templates' && (
          <div className="mt-[28px]">
            <Controller
              name="stepTwo.templates"
              control={control}
              rules={{
                validate: values =>
                  values.length > 0 ? undefined : 'Please select at least one template',
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <>
                  <TemplatePicker
                    selectedTemplateIds={value.map(template => template.id)}
                    onSelect={value => {
                      onChange({ target: { value } })
                    }}
                  />
                  <FormError error={error} />
                </>
              )}
            />
          </div>
        )}

        {dataType === 'files' && (
          <div className="mt-[28px]">
            <Controller
              name="stepTwo.files"
              control={control}
              rules={{ validate: validateFiles }}
              render={({ field: { value, onChange }, fieldState: { error } }) => {
                const isDragNDropError =
                  error?.message === FILE_VALIDATION_ERROR.COUNT ||
                  error?.message === FILE_VALIDATION_ERROR.MAX_COUNT

                return (
                  <>
                    <DragNDrop
                      accept=".csv,text/csv"
                      title={
                        <Trans>
                          <span className="font-semibold">Click or drag and drop</span> to upload
                          data dictionary file along with any list values.
                        </Trans>
                      }
                      onFilesChange={(files: File[]) => {
                        onChange({ target: { value: [...value, ...files] } })
                      }}
                      isError={isDragNDropError}
                    />
                    {isDragNDropError && <FormError error={error} />}

                    <div className="mt-[20px]">
                      {!!value?.length &&
                        value.map((file, index: number) => {
                          const isError =
                            (!file.objective &&
                              error?.message === FILE_VALIDATION_ERROR.EMPTY_OBJECTIVE) ||
                            error?.message === FILE_VALIDATION_ERROR.SAME_OBJECTIVE ||
                            error?.message === FILE_VALIDATION_ERROR.DATA_DICTIONARY_REQUIRED

                          return (
                            <>
                              <FileCard
                                key={file.name + String(index)}
                                title={file.name}
                                subtitle={getFileSizeText(file.size)}
                                objective={file.objective}
                                className={cn({ 'mb-4': !isError })}
                                isError={isError}
                                onRemoveClick={() => {
                                  const newFiles = [...value]
                                  newFiles.splice(index, 1)

                                  onChange({ target: { value: newFiles } })
                                }}
                                onObjectiveChange={objective => {
                                  const newFiles = [...value]
                                  if (!newFiles[index]) {
                                    return
                                  }

                                  newFiles[index].objective = objective

                                  onChange({ target: { value: newFiles } })
                                }}
                              />
                              {isError && <FormError error={error} className="mb-4" />}
                            </>
                          )
                        })}
                    </div>
                  </>
                )
              }}
            />

            <StepsFileInfo />
          </div>
        )}

        <div className="grow" />
        <StepsInfo />
      </div>
    </div>
  )
}
